'use client';

/**
 * Query Provider
 *
 * Provides TanStack Query context for server state management.
 * Enables useQuery hooks throughout the app for data fetching.
 *
 * [Source: docs/architecture.md#State-Management-(Pulse)]
 * [Source: docs/stories/4-2-implement-site-list-view.md#Task-2]
 */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

interface QueryProviderProps {
  children: ReactNode;
}

export function QueryProvider({ children }: QueryProviderProps) {
  // Create QueryClient in state to avoid sharing between requests
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Data is considered stale after 30 seconds
            staleTime: 30_000,
            // Retry failed requests up to 2 times
            retry: 2,
            // Don't refetch on window focus by default
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}
